/** @jsx jsx */
import { jsx } from '@emotion/core'
import {
  FunctionComponent,
  useState,
  useMemo,
  ReactElement,
  AnchorHTMLAttributes,
} from 'react'
import { Link } from '@reach/router'
import { Bell, User, LogOut } from 'react-feather'

import { Notification } from '~/components'
import { TNotification } from '~/types'
import { styles } from './styles'
import { Placeholder } from '../placeholder'
import { Dropdown } from '../dropdown'
import { UserStore, NotificationsStore, AnalyticsStore } from '~/stores'
import { useObserver } from 'mobx-react-lite'

const activeLinkProps = {
  style: {
    color: 'rgb(58, 137, 196)',
    borderBottom: '3px solid rgb(58, 137, 196)',
  },
}

interface NavbarProps {
  userStore: UserStore
  notificationsStore: NotificationsStore
}

export const Navbar: FunctionComponent<NavbarProps> = ({
  userStore,
  notificationsStore,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)

  useMemo(() => {
    if (notificationsOpen) {
      AnalyticsStore.event('notifications', 'open-notifications')
    }
  }, [notificationsOpen])

  return useObserver(() => (
    <header css={styles.container}>
      <div css={styles.section}>
        <Link to="/requests" css={styles.item}>
          <h1 css={styles.title.container}>
            <span css={styles.title.name}>partscout</span>
            <span css={styles.title.ext}>.me</span>
          </h1>
        </Link>
      </div>

      <section css={styles.section}>
        <Link
          to="/requests/new"
          getProps={(props) => props.isCurrent && activeLinkProps}
          css={[styles.item, styles.button]}
        >
          <img src={require('~/assets/plus-circle.svg')} alt="plus circle icon" />
          <span>NEW REQUEST</span>
        </Link>

        <div css={styles.separator}></div>

        <Link
          to="/requests"
          css={styles.item}
          getProps={(props) => props.isCurrent && activeLinkProps}
        >
          REQUESTS
        </Link>
        <Link
          to="/requests/my"
          css={styles.item}
          getProps={(props) => props.isCurrent && activeLinkProps}
        >
          MY REQUESTS
        </Link>
        <Link
          to="/offers/my"
          css={styles.item}
          getProps={(props) => props.isCurrent && activeLinkProps}
        >
          MY OFFERS
        </Link>
        <Link
          to="/why"
          css={styles.item}
          getProps={(props) => props.isCurrent && activeLinkProps}
        >
          WHY
        </Link>

        <div css={styles.separator}></div>

        <div css={styles.item} onClick={() => setNotificationsOpen(true)}>
          <Bell
            size={20}
            strokeWidth={2.4}
            color={notificationsStore.unseenNotificationsCount ? '#666666' : '#777777'}
          />

          {notificationsStore.unseenNotificationsCount ? (
            <div css={styles.marker}>{notificationsStore.unseenNotificationsCount}</div>
          ) : (
            ''
          )}

          {notificationsOpen && (
            <Dropdown width={300} onClose={() => setNotificationsOpen(false)}>
              {notificationsStore.notifications?.length ? (
                notificationsStore.sortedNotifications.map((notification) => (
                  <Notification
                    notification={notification}
                    onSeen={() => {
                      notificationsStore.setNotificationAsSeen(notification.ID)
                      setNotificationsOpen(false)
                    }}
                    onDelete={() =>
                      notificationsStore.deleteNotification(notification.ID)
                    }
                    key={notification.ID}
                  />
                ))
              ) : (
                <Placeholder>You have no new notifications</Placeholder>
              )}
            </Dropdown>
          )}
        </div>

        <div css={styles.separator}></div>

        <div css={styles.item} onClick={() => setMenuOpen(!menuOpen)}>
          <div css={styles.avatar}>
            <User width="16px" height="16px" strokeWidth={2.4} color="#777777" />
          </div>

          <span>{userStore.userAttributesObject.email}</span>

          <img
            src={require('~/assets/chevron-down.svg')}
            width={18}
            height={18}
            style={{ marginLeft: 7 }}
            alt="chevron down icon"
          />

          {menuOpen && (
            <Dropdown width={200} onClose={() => setMenuOpen(false)}>
              <MenuItem
                to="/profile"
                title="PROFILE"
                icon={<User size={16} strokeWidth={2.4} color="#777777" />}
              />
              <MenuItem
                to="#"
                title="SIGN OUT"
                icon={<LogOut size={16} strokeWidth={2.4} color="#777777" />}
                onClick={userStore.signOut}
              />
            </Dropdown>
          )}
        </div>
      </section>
    </header>
  ))
}

interface MenuItemProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string
  title: string
  icon: ReactElement
}

const MenuItem: FunctionComponent<MenuItemProps> = ({ to, title, icon, ...props }) => (
  <Link to={to} css={styles.menuItem} {...props}>
    {icon}
    <span>{title}</span>
  </Link>
)
